import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { AuthModal } from '@/components/auth/AuthModal';
import { useAuth } from '@/lib/auth';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Calendar, MapPin, Users, Clock, ArrowDown, Play, Pause, Volume2, VolumeX, BedDouble, Coffee, Sparkles, Utensils, Ship, Sunset, ChevronLeft, ChevronRight, Anchor, Globe, ExternalLink, Tent, Star, Music } from 'lucide-react';
import { ItineraryCarousel } from '@/components/experiences/ItineraryCarousel';
import { useLocation } from 'react-router-dom';
import { MetaTags } from '@/components/MetaTags';

export function AegeanOdysseyVoyage2() {
  const { user } = useAuth();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [freezeFrameShowing, setFreezeFrameShowing] = useState(true);
  const [pageLoaded, setPageLoaded] = useState(false);
  
// Image gallery for accommodation
  const accommodationImages = [
    "https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/Catamaran%20Lodging.png",
    "https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/20240229-untitled-14-34-07.jpg",
    "https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/20240229-untitled-14-57-53-Enhanced-NR.jpg",
    "https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/20240229-untitled-16-23-54.jpg",
  ];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Mark page as loaded
  useEffect(() => {
    setPageLoaded(true);
  }, []);
  
  // Initialize Google Maps
  useEffect(() => {
    const mapElement = document.getElementById('venue-map');
    if (!mapElement || !window.google?.maps) return;

    const map = new google.maps.Map(mapElement, {
      center: { lat: 37.4415, lng: 25.3667 }, // Center on general Aegean region
      zoom: 7,
      mapTypeId: "roadmap",
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
        style: google.maps.ZoomControlStyle.SMALL
      }
    });

    // Define locations in order of journey
    const locations = [
      { position: { lat: 38.3088, lng: 26.6337 }, label: '1', title: "Bonjuk Bay - Starting point" },
      { position: { lat: 36.6173, lng: 27.8428 }, label: '2', title: "Simi - Pastel mansions" },
      { position: { lat: 36.4226, lng: 27.3532 }, label: '3', title: "Tilos - Untouched nature" },
      { position: { lat: 36.5786, lng: 26.3509 }, label: '4', title: "Astypalaia - Whitewashed houses" },
      { position: { lat: 37.1576, lng: 24.4894 }, label: '5', title: "Serifos - Rugged beauty" },
      { position: { lat: 37.2604, lng: 23.1614 }, label: '6', title: "Spetses - Pine-scented breezes" },
      { position: { lat: 37.3361, lng: 23.4594 }, label: '7', title: "Hydra - Timeless charm" },
      { position: { lat: 37.9838, lng: 23.7275 }, label: '8', title: "Athens - Ancient ruins and modern energy" }
    ];
    
    // Create markers for each location with numbered labels
    locations.forEach(location => {
      new google.maps.Marker({
        position: location.position,
        map,
        label: location.label,
        title: location.title
      });
    });
    
    // Create lines to show the journey path
    const journeyPath = new google.maps.Polyline({
      path: locations.map(location => location.position),
      geodesic: true,
      strokeColor: "#FF5722",
      strokeOpacity: 0.8,
      strokeWeight: 3
    });
    
    journeyPath.setMap(map);

    // Create info window to display when marker is clicked
    const infoWindow = new google.maps.InfoWindow();

    // Add click listeners to markers
    locations.forEach((location, i) => {
      const marker = new google.maps.Marker({
        position: location.position,
        map,
        label: location.label,
        title: location.title
      });
      
      marker.addListener("click", () => {
        infoWindow.setContent(`
          <div style="padding: 8px; max-width: 200px;">
            <h3 style="margin: 0 0 8px; font-weight: bold;">${location.title.split(' - ')[0]}</h3>
            <p style="margin: 0; font-size: 14px;">${location.title.split(' - ')[1]}</p>
            <p style="margin: 5px 0 0; font-size: 12px; color: #666;">Stop ${i + 1} of ${locations.length}</p>
          </div>
        `);
        infoWindow.open(map, marker);
      });
    });
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !pageLoaded) return;

    // Preload video data
    video.preload = "auto";
    
    // Handle video loading states
    const handleLoadedMetadata = () => {
      setIsVideoLoaded(true);
      
      // Start the video 2.5 seconds after the page loads
      setTimeout(() => {
        setFreezeFrameShowing(false);
        video.play()
          .then(() => setIsPlaying(true))
          .catch(err => {
            console.warn('Autoplay failed:', err);
            setIsPlaying(false);
          });
      }, 2500);
    };

    // Handle visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!freezeFrameShowing) { // Only auto-play if freeze frame is not showing
          video.play().catch(err => console.warn('Visibility play failed:', err));
          setIsPlaying(true);
        }
      } else {
        video.pause();
        setIsPlaying(false);
      }
    };

    // Add event listeners
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    video.addEventListener('play', () => setIsPlaying(true));
    video.addEventListener('pause', () => setIsPlaying(false));

    // Start loading the video
    video.load();

    // Cleanup
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      video.removeEventListener('play', () => setIsPlaying(true));
      video.removeEventListener('pause', () => setIsPlaying(false));
    };
  }, [pageLoaded]);

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    // If we're showing the freeze frame, cancel it and play immediately
    if (freezeFrameShowing) {
      setFreezeFrameShowing(false);
    }

    if (video.paused) {
      video.play()
        .then(() => setIsPlaying(true))
        .catch(error => {
          console.error('Play failed:', error);
          setIsPlaying(false);
        });
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handlePurchaseClick = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    
    // Open contact email in mail client
    window.location.href = "mailto:contact@space-time.travel?subject=Aegean%20Odyssey%20Inquiry&body=I'm%20interested%20in%20the%20Aegean%20Odyssey%20experience%20(June%202-9,%202025).%20Please%20send%20me%20more%20information.";
  };

  const scrollToContent = () => {
    const contentSection = document.getElementById('content-section');
    if (contentSection) {
      contentSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Image gallery navigation
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === accommodationImages.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? accommodationImages.length - 1 : prevIndex - 1
    );
  };

  return (
    <div className="relative min-h-screen bg-earth-900">
      <MetaTags 
        title="Aegean Odyssey: Sailing + Festival | rendezvous"
        description="Sail the Greek Islands and experience the Per Anhalter Festival in this unique dual adventure. June 2-9, 2025."
        image="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/221002_2_timbertotide%20(1).jpg"
        url="https://letsrendezvous.co/experiences/aegean2"
      />

      {/* Hero Section with Video */}
      <div className="relative w-full h-[100vh] overflow-hidden pt-10">
        {/* Hero Video Container with Center Alignment */}
        <div className="absolute inset-0 top-20 flex justify-center items-center bg-earth-900 overflow-hidden">
          {/* Poster Image/Freeze Frame */}
          <div 
            className={`absolute inset-0 z-10 transition-opacity duration-500 ${freezeFrameShowing ? 'opacity-100' : 'opacity-0'}`}
          >
            <img
              src="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/221002_2_timbertotide%20(1).jpg"
              alt="Aegean Odyssey"
              className="w-full h-full object-cover object-center"
            />
          </div>
          
          {/* Video Element */}
          <video
            ref={videoRef}
            src="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/short%20catemaran%20drone.mp4"
            className={`w-full h-full object-cover transition-opacity duration-1000 ${freezeFrameShowing ? 'opacity-0' : 'opacity-100'}`}
            muted={isMuted}
            loop
            playsInline
            poster="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/221002_2_timbertotide%20(1).jpg"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-earth-900/50 to-earth-900" />

          {/* Video Controls */}
          <div className="absolute bottom-10 right-10 flex items-center gap-4 z-20">
            <button
              onClick={handlePlayPause}
              className="p-3 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800 transition-colors"
            >
              {isPlaying && !freezeFrameShowing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
            </button>
            <button
              onClick={handleToggleMute}
              className="p-3 bg-earth-900/80 backdrop-blur-sm rounded-full text-sand-100 hover:bg-earth-800 transition-colors"
            >
              {isMuted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="absolute inset-0 top-20 flex flex-col items-center justify-end px-4 text-center pb-36">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display mb-6">Aegean Odyssey Voyage 2</h1>
            <p className="text-xl md:text-2xl text-sand-200 mb-8">Return through the Greek Islands</p>
            
            
          </motion.div>

          {/* Scroll Down Indicator */}
          <motion.div
            className="absolute bottom-12 left-0 right-0 flex justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            <button 
              onClick={scrollToContent}
              className="flex flex-col items-center text-sand-300 hover:text-sand-100 transition-colors"
            >
              <span className="text-sm uppercase tracking-widest mb-2">Discover</span>
              <ArrowDown className="h-6 w-6 animate-bounce" />
            </button>
          </motion.div>
        </div>
      </div>

      {/* Content Section with Tabs */}
      <div id="content-section" className="container mx-auto px-4 py-24">
        <div className="max-w-4xl mx-auto">
          {/* Sidebar Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <Tabs defaultValue="about" className="space-y-8">
                <TabsList className="w-full">
                  <TabsTrigger value="about">About</TabsTrigger>
                  <TabsTrigger value="itinerary">Itinerary</TabsTrigger>
                  <TabsTrigger value="accommodation">Accommodation</TabsTrigger>
                  <TabsTrigger value="location">Location</TabsTrigger>
                  <TabsTrigger value="faq">FAQ</TabsTrigger>
                </TabsList>
                
                <TabsContent value="about" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">About the Experience</h2>
                  <div className="space-y-4 text-sand-300">
                    <p>
                      At Space-Time-Architects, we create boutique journeys that explore the world in all its dimensions. Set sail with us on a voyage where time stretches, the sea leads the way, and each day brings moments of connection, wonder, and discovery. From hidden coves to sun-soaked meals with fellow travelers, our journeys are woven with meaning, beauty, and the spirit of exploration.
                    </p>
                    <p>
                      From Bonjuk Bay, we set sail into the Aegean, chasing the perfect rhythm of sun, sea, and discovery. Each island stop brings a new story — hidden beaches, late-night conversations, and moments that feel like they belong to another world. Days blur between sailing, exploring, and dancing under the stars, where freedom is the only plan. As we reach Athens, the journey lingers — sealed in radiance, sun, and memories that won't fade.
                    </p>
                    
                    <div className="pt-4">
                      <h3 className="text-xl font-display mb-3">What's Included</h3>
                      <ul className="list-disc list-inside space-y-2">
                        <li>Cultural excursions to scenic villages and historic sites</li>
                        <li>Sunbathing at secluded beaches and hidden coves</li>
                        <li>Water sports activities and swimming in crystal clear waters</li>
                        <li>Hidden islands adventures off the typical tourist path</li>
                        <li>All meals curated by a three-star Michelin chef during sailing</li>
                        <li>All accommodations during the sailing portion</li>
                        <li>Transportation between islands</li>
                      </ul>
                    </div>
                  </div>
                  
                  <img 
                    src="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/20240301-untitled-16-43-19.jpg" 
                    alt="Greek Islands Sailing" 
                    className="w-full h-auto rounded-lg mt-6"
                  />
                </TabsContent>
                
                <TabsContent value="itinerary" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Journey Itinerary</h2>
                  <p className="text-sand-300 mb-6">
                    <strong>JOURNEY: 2 June - 9 June 2025</strong>
                  </p>
                  <div className="space-y-8">
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-4">Sailing the Greek Islands</h3>
                      <p className="text-sand-300 mb-4">
                        From Bonjuk Bay, we set sail into the Aegean, chasing the perfect rhythm of sun, sea, and discovery. Each island stop brings a new story — hidden beaches, late-night conversations, and moments that feel like they belong to another world. Days blur between sailing, exploring, and dancing under the stars, where freedom is the only plan. As we reach Athens, the journey lingers — sealed in radiance, sun, and memories that won't fade.
                      </p>
                    </div>
                    
                    <div className="space-y-6">
                      <h3 className="text-xl font-display mb-2">Island Destinations</h3>
                      
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="md:w-1/3">
                            <img 
                              src="https://images.pexels.com/photos/3601426/pexels-photo-3601426.jpeg" 
                              alt="Bonjuk Bay" 
                              className="w-full h-40 object-cover rounded-lg"
                            />
                          </div>
                          <div className="md:w-2/3">
                            <h4 className="font-medium text-sand-100 mb-2">Bonjuk Bay</h4>
                            <p className="text-sand-300">A convergence of celebration and reflection. This secluded bay is our starting point, where we'll gather before beginning our sailing adventure.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="md:w-1/3">
                            <img 
                              src="https://images.pexels.com/photos/1010657/pexels-photo-1010657.jpeg" 
                              alt="Simi" 
                              className="w-full h-40 object-cover rounded-lg"
                            />
                          </div>
                          <div className="md:w-2/3">
                            <h4 className="font-medium text-sand-100 mb-2">Simi</h4>
                            <p className="text-sand-300">A sun-drenched gem where pastel mansions meet the shimmering sea, exuding timeless island charm.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="md:w-1/3">
                            <img 
                              src="https://images.pexels.com/photos/1021075/pexels-photo-1021075.jpeg" 
                              alt="Tilos" 
                              className="w-full h-40 object-cover rounded-lg"
                            />
                          </div>
                          <div className="md:w-2/3">
                            <h4 className="font-medium text-sand-100 mb-2">Tilos</h4>
                            <p className="text-sand-300">A hidden sanctuary of untouched nature, quiet beaches, and a soul that beats to the rhythm of the wild.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="md:w-1/3">
                            <img 
                              src="https://images.pexels.com/photos/4388167/pexels-photo-4388167.jpeg" 
                              alt="Astypalaia" 
                              className="w-full h-40 object-cover rounded-lg"
                            />
                          </div>
                          <div className="md:w-2/3">
                            <h4 className="font-medium text-sand-100 mb-2">Astypalaia</h4>
                            <p className="text-sand-300">Where whitewashed houses cling to the cliffs, the Aegean sparkles below, and time slows to the island's gentle rhythm.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="md:w-1/3">
                            <img 
                              src="https://images.pexels.com/photos/1450361/pexels-photo-1450361.jpeg" 
                              alt="Serifos" 
                              className="w-full h-40 object-cover rounded-lg"
                            />
                          </div>
                          <div className="md:w-2/3">
                            <h4 className="font-medium text-sand-100 mb-2">Serifos</h4>
                            <p className="text-sand-300">A rugged beauty of sun-bleached hills, golden beaches, and a raw, untamed spirit that whispers through the wind.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="md:w-1/3">
                            <img 
                              src="https://images.pexels.com/photos/13781002/pexels-photo-13781002.jpeg" 
                              alt="Spetses" 
                              className="w-full h-40 object-cover rounded-lg"
                            />
                          </div>
                          <div className="md:w-2/3">
                            <h4 className="font-medium text-sand-100 mb-2">Spetses</h4>
                            <p className="text-sand-300">Where pine-scented breezes, elegant mansions, and a vibrant maritime soul create an island of timeless charm and adventure.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="md:w-1/3">
                            <img 
                              src="https://images.pexels.com/photos/1285625/pexels-photo-1285625.jpeg" 
                              alt="Hydra" 
                              className="w-full h-40 object-cover rounded-lg"
                            />
                          </div>
                          <div className="md:w-2/3">
                            <h4 className="font-medium text-sand-100 mb-2">Hydra</h4>
                            <p className="text-sand-300">A car-free paradise where donkeys navigate stone pathways, artists find inspiration, and the harbor reflects centuries of maritime glory.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-earth-800/50 p-6 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-6">
                          <div className="md:w-1/3">
                            <img 
                              src="https://images.pexels.com/photos/951539/pexels-photo-951539.jpeg" 
                              alt="Athens" 
                              className="w-full h-40 object-cover rounded-lg"
                            />
                          </div>
                          <div className="md:w-2/3">
                            <h4 className="font-medium text-sand-100 mb-2">Athens</h4>
                            <p className="text-sand-300">Where ancient ruins and modern energy collide, weaving history, culture, and endless possibility into the heartbeat of the city.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-4">Day-by-Day Journey</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-sand-100">Day 1: Bonjuk Bay</h4>
                          <p className="text-sand-300">We gather in this beautiful bay, meet our fellow travelers, and prepare for our sailing adventure.</p>
                        </div>
                        
                        <div>
                          <h4 className="font-medium text-sand-100">Day 2-7: Island Hopping</h4>
                          <p className="text-sand-300">Sailing through the Greek islands, exploring ancient ruins, swimming in crystal clear waters, and enjoying meals prepared by our Michelin-starred chef.</p>
                        </div>
                        
                        <div>
                          <h4 className="font-medium text-sand-100">Day 8: Athens</h4>
                          <p className="text-sand-300">Our journey concludes in the historic city of Athens, where ancient ruins and modern energy collide.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="accommodation" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Accommodation Options</h2>
                  <p className="text-sand-300 mb-8">
                    Our journey features two distinct accommodation experiences: luxurious sailing accommodations during the Greek Islands portion, and your choice of accommodations during the festival portion in Turkey.
                  </p>
                  
                  {/* Image Gallery */}
                  <div className="relative rounded-lg overflow-hidden aspect-video mb-8">
                    <img 
                      src={accommodationImages[currentImageIndex]}
                      alt="Accommodation"
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Navigation Arrows */}
                    <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4">
                      <button
                        onClick={(e) => { e.preventDefault(); prevImage(); }}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-black/50 text-white"
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </button>
                      
                      <button
                        onClick={(e) => { e.preventDefault(); nextImage(); }}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-black/50 text-white"
                      >
                        <ChevronRight className="h-6 w-6" />
                      </button>
                    </div>
                    
                    {/* Image Indicators */}
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                      {accommodationImages.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`w-2 h-2 rounded-full ${currentImageIndex === index ? 'bg-white' : 'bg-white/50'}`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-8">
                    {/* Sailing Accommodation Details - No separate image now */}
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-2xl font-display mb-4">Catamaran Accommodation</h3>
                      <div className="flex items-center gap-2 text-sand-300 mb-4">
                        <Anchor className="h-5 w-5" />
                        <span>Private cabin on a luxurious catamaran</span>
                      </div>
                      <p className="text-sand-300 mb-4">
                        During the journey, you'll stay aboard our premium catamaran featuring spacious cabins with ensuite bathrooms, ample deck space for relaxation, and all the comforts needed for a luxurious journey at sea.
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center gap-2 text-sand-300">
                          <BedDouble className="h-4 w-4" />
                          <span>Private Cabins</span>
                        </div>
                        <div className="flex items-center gap-2 text-sand-300">
                          <Coffee className="h-4 w-4" />
                          <span>Ensuite Bathrooms</span>
                        </div>
                        <div className="flex items-center gap-2 text-sand-300">
                          <Utensils className="h-4 w-4" />
                          <span>Chef-Prepared Meals</span>
                        </div>
                        <div className="flex items-center gap-2 text-sand-300">
                          <Sunset className="h-4 w-4" />
                          <span>Spacious Sundeck</span>
                        </div>
                      </div>
                    </div>

                    {/* Festival Accommodation Options */}
                    <h3 className="text-xl font-display mb-4">Catamaran Stay Options</h3>
                    
                    {/* Option 1: Catamaran - Wide image with text below */}
                    <div className="bg-earth-800/50 rounded-lg overflow-hidden">
                      <img 
                        src="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/20240229-untitled-16-19-59-2-Enhanced-NR.jpg" 
                        alt="Anchored Catamaran" 
                        className="w-full h-64 object-cover"
                      />
                      <div className="p-6">
                        <h4 className="text-xl font-display mb-2">Catamaran Stay Option</h4>
                        <p className="text-sand-300 mb-4">
                          Your cabin remains yours throughout the festival. A shuttle will bring you between the boat and festival area, giving you the comfort of a familiar space while enjoying all festival activities.
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center gap-2 text-sand-300">
                            <Ship className="h-4 w-4" />
                            <span>Familiar Accommodation</span>
                          </div>
                          <div className="flex items-center gap-2 text-sand-300">
                            <MapPin className="h-4 w-4" />
                            <span>Shuttle Service Included</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg mt-6">
                      <h3 className="text-xl font-display mb-4">Dining Experience</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium text-sand-100 mb-2">Onboard</h4>
                          <p className="text-sand-300">All meals are included while sailing, curated by a three-star Michelin chef who will prepare a variety of Mediterranean-inspired dishes using fresh, local ingredients.</p>
                        </div>
                        <div>
                          <h4 className="font-medium text-sand-100 mb-2">At the Festival</h4>
                          <p className="text-sand-300">Meals available at an additional cost (€60 per day), featuring a range of options to suit all dietary preferences.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="location" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Journey Route</h2>
                  <div className="space-y-8">
                    <div className="bg-earth-800/50 p-6 rounded-lg mb-6">
                      <p className="text-sand-300 mb-4">
                        Our Aegean Odyssey takes you on a carefully crafted route through the most stunning locations in the Aegean Sea. Follow our journey chronologically from Bonjuk Bay (1) through the islands to Athens (8).
                      </p>
                      <p className="text-sand-300">
                        Click on each numbered marker on the map to learn more about each destination.
                      </p>
                    </div>
                    
                    <div className="aspect-[16/9] rounded-lg overflow-hidden">
                      <div id="venue-map" className="w-full h-full rounded-lg"></div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <h3 className="text-lg font-display mb-2 flex items-center">
                          <span className="w-6 h-6 bg-white text-black rounded-full inline-flex items-center justify-center mr-2 text-sm font-bold">1</span>
                          Bonjuk Bay, Turkey
                        </h3>
                        <p className="text-sand-300">
                          Our journey begins in this magical bay, where we'll gather before setting sail on our Aegean adventure.
                        </p>
                      </div>
                      
                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <h3 className="text-lg font-display mb-2 flex items-center">
                          <span className="w-6 h-6 bg-white text-black rounded-full inline-flex items-center justify-center mr-2 text-sm font-bold">2</span>
                          Simi, Greece
                        </h3>
                        <p className="text-sand-300">
                          A sun-drenched gem where pastel mansions meet the shimmering sea, exuding timeless island charm.
                        </p>
                      </div>
                      
                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <h3 className="text-lg font-display mb-2 flex items-center">
                          <span className="w-6 h-6 bg-white text-black rounded-full inline-flex items-center justify-center mr-2 text-sm font-bold">3</span>
                          Tilos, Greece
                        </h3>
                        <p className="text-sand-300">
                          A hidden sanctuary of untouched nature, quiet beaches, and a soul that beats to the rhythm of the wild.
                        </p>
                      </div>
                      
                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <h3 className="text-lg font-display mb-2 flex items-center">
                          <span className="w-6 h-6 bg-white text-black rounded-full inline-flex items-center justify-center mr-2 text-sm font-bold">4</span>
                          Astypalaia, Greece
                        </h3>
                        <p className="text-sand-300">
                          Where whitewashed houses cling to the cliffs, the Aegean sparkles below, and time slows to the island's gentle rhythm.
                        </p>
                      </div>
                      
                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <h3 className="text-lg font-display mb-2 flex items-center">
                          <span className="w-6 h-6 bg-white text-black rounded-full inline-flex items-center justify-center mr-2 text-sm font-bold">5</span>
                          Serifos, Greece
                        </h3>
                        <p className="text-sand-300">
                          A rugged beauty of sun-bleached hills, golden beaches, and a raw, untamed spirit that whispers through the wind.
                        </p>
                      </div>
                      
                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <h3 className="text-lg font-display mb-2 flex items-center">
                          <span className="w-6 h-6 bg-white text-black rounded-full inline-flex items-center justify-center mr-2 text-sm font-bold">6</span>
                          Spetses, Greece
                        </h3>
                        <p className="text-sand-300">
                          Where pine-scented breezes, elegant mansions, and a vibrant maritime soul create an island of timeless charm and adventure.
                        </p>
                      </div>
                      
                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <h3 className="text-lg font-display mb-2 flex items-center">
                          <span className="w-6 h-6 bg-white text-black rounded-full inline-flex items-center justify-center mr-2 text-sm font-bold">7</span>
                          Hydra, Greece
                        </h3>
                        <p className="text-sand-300">
                          A car-free paradise where donkeys navigate stone pathways, artists find inspiration, and the harbor reflects centuries of maritime glory.
                        </p>
                      </div>
                      
                      <div className="bg-earth-800/50 p-4 rounded-lg">
                        <h3 className="text-lg font-display mb-2 flex items-center">
                          <span className="w-6 h-6 bg-white text-black rounded-full inline-flex items-center justify-center mr-2 text-sm font-bold">8</span>
                          Athens, Greece
                        </h3>
                        <p className="text-sand-300">
                          Where ancient ruins and modern energy collide, weaving history, culture, and endless possibility into the heartbeat of the city.
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-4">Travel Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium text-sand-100 mb-2">Getting There</h4>
                          <ul className="space-y-2 text-sand-300">
                            <li className="flex items-start gap-2">
                              <span className="text-sand-100">•</span> 
                              <span>Fly to Izmir Airport to reach Bonjuk Bay</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-sand-100">•</span> 
                              <span>Return from Athens International Airport (ATH)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-sand-100">•</span> 
                              <span>Airport transfers included</span>
                            </li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-medium text-sand-100 mb-2">Visa Requirements</h4>
                          <ul className="space-y-2 text-sand-300">
                            <li className="flex items-start gap-2">
                              <span className="text-sand-100">•</span> 
                              <span>Check visa requirements for both Turkey and Greece</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-sand-100">•</span> 
                              <span>Many nationalities can obtain e-Visas for Turkey online</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-sand-100">•</span> 
                              <span>We'll provide guidance after booking</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="faq" className="space-y-6">
                  <h2 className="text-3xl font-display mb-6">Frequently Asked Questions</h2>
                  <div className="space-y-6">
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">Do I need sailing experience?</h3>
                      <p className="text-sand-300">
                        No sailing experience is necessary. Our professional captain and crew handle all technical aspects of sailing. You're welcome to participate and learn as much as you'd like, or simply relax and enjoy the journey. This experience is designed to be accessible to everyone, regardless of prior sailing knowledge.
                      </p>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">What should I pack?</h3>
                      <p className="text-sand-300">
                        For the sailing portion: lightweight clothing, swimwear, sun protection (hat, sunglasses, sunscreen), a light jacket for evenings, and non-marking deck shoes. We'll provide a detailed packing list upon booking.
                      </p>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">How many guests will be on this journey?</h3>
                      <p className="text-sand-300">
                        To ensure an intimate and personalized experience, our journey is limited to 16 participants. This allows for genuine connections between guests and ensures everyone receives attentive service from our crew and guides.
                      </p>
                    </div>
                    
                    <div className="bg-earth-800/50 p-6 rounded-lg">
                      <h3 className="text-xl font-display mb-2">What is the cancellation policy?</h3>
                      <p className="text-sand-300">
                        We offer a full refund if canceled 90 days before departure, 50% refund if canceled 60-89 days before departure, and no refund for cancellations less than 60 days before departure. We strongly recommend comprehensive travel insurance that includes cancellation coverage.
                      </p>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="sticky top-24 space-y-6">
                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-display">Price</h3>
                    <div className="text-2xl font-display">€5,500pp</div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sand-300">
                      <Clock className="h-5 w-5 text-sand-400" />
                      <span>8 days, 7 nights</span>
                    </div>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Calendar className="h-5 w-5 text-sand-400" />
                      <span>June 2 - June 9, 2025</span>
                    </div>
                    <div className="flex items-center gap-2 text-sand-300">
                      <Users className="h-5 w-5 text-sand-400" />
                      <span>Limited to 16 participants</span>
                    </div>
                    <Button
                      className="w-full mt-4"
                      onClick={handlePurchaseClick}
                    >
                      Request to Book
                    </Button>
                  </div>
                </div>

                {/* Creator Information - Added similar to Xamanic page */}
                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">Creator</h3>
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src="https://rendezvouswebsite.b-cdn.net/Space%20Time%20Architects/Tile%20Flyer.png" 
                      alt="Space Time Architects"
                      className="w-16 h-16 rounded-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "https://api.dicebear.com/7.x/initials/svg?seed=STA&backgroundColor=1d1918&textColor=e8e4dc";
                      }}
                    />
                    <div>
                      <p className="text-sand-100 font-medium">Space Time Architects</p>
                      <p className="text-sand-400 text-sm">Experience Creators</p>
                    </div>
                  </div>
                  <p className="text-sand-300 text-sm mb-4">
                    Space Time Architects designs transformative experiences that blend adventure, culture, and meaningful connection. Specializing in unique journeys that combine sailing, festivals, and exploration, they create moments that transcend ordinary travel.
                  </p>
                  <a 
                    href="https://space-time.travel" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sand-300 hover:text-sand-200 transition-colors"
                  >
                    <Globe className="h-4 w-4" />
                    <span className="underline">space-time.travel</span>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                
                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">What's Included</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Ship className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">7 days aboard luxury catamaran</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Utensils className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">All meals during sailing by three-star Michelin chef</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <BedDouble className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Accommodation on catamaran</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">All transfers and transport</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Anchor className="h-5 w-5 text-sand-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sand-300">Professional crew and guides</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-earth-800/50 p-6 rounded-lg">
                  <h3 className="text-xl font-display mb-4">Not Included</h3>
                  <ul className="space-y-3 text-sand-300">
                    <li className="flex items-start gap-3">
                      <span className="text-sand-100">•</span> 
                      <span>Flights to Izmir and from Athens</span>
                    </li>                
                    <li className="flex items-start gap-3">
                      <span className="text-sand-100">•</span> 
                      <span>Travel insurance (strongly recommended)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          {/* Call to Action */}
          <div className="mt-24 bg-earth-800/50 p-8 rounded-lg text-center">
            <h2 className="text-3xl font-display mb-6">Begin Your Aegean Odyssey</h2>
            <p className="text-sand-300 mb-8 max-w-2xl mx-auto">
              This unique experience combines luxury sailing through the Greek Islands starting from Bonjuk Bay and ending in Athens. Limited spaces available for this exclusive journey.
            </p>
            <Button 
              size="lg"
              onClick={handlePurchaseClick}
              className="text-lg px-8 py-4"
            >
              Request to Book
            </Button>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultTab="sign-up"
        redirectTo={location.pathname}
      />
    </div>
  );
}